/**
 * Inquilinos. Rejilla de 3 columnas de fichas.
 *
 * El distintivo «Contrato ✓» llega DERIVADO de la tabla `documents` y el
 * estado de pago, del rent roll. Nada de eso es un campo almacenado en `tenants`.
 *
 * QUIÉN SIGUE Y QUIÉN NO LO DICE EL CONTRATO, no una casilla en la ficha del inquilino. Es la
 * única versión que no puede mentir: si «vigente» fuera un campo suelto, rescindir un contrato
 * y olvidarse de desmarcarlo dejaría a alguien vigente en una habitación que ya ocupa otro.
 * Por eso aquí el estado se PINTA y se FILTRA, y se cambia donde se decide —activando,
 * renovando o rescindiendo el contrato, en la ficha del inquilino—.
 *
 * El filtro por defecto son los ACTUALES, porque mezclar a quien se fue hace dos meses
 * enseñando la habitación que hoy tiene otro es peor que no mostrarlo. Quien ya no vive aquí, y
 * los DADOS DE BAJA, se ven pulsando «Todos», mezclados con el resto y con su etiqueta. Borrar a
 * alguien se hace desde DENTRO de su ficha, nunca desde aquí.
 *
 * DOS VISTAS, REJILLA Y TABLA, y el estado de las tres cosas —vista, histórico y búsqueda—
 * vive en la URL. Con dieciséis fichas la rejilla es más agradable; con doscientas, lo que se
 * quiere es comparar filas y ordenar con la vista. Y que el enlace se pueda enviar tal cual:
 * «mira los históricos de Calle Mayor» es una URL, no una lista de instrucciones.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { IdDocumentType, LeaseStatus, RotationResponse, RotationRow, TenantCard, TenantPaymentStatus } from '@naar/shared';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import { Badge, type BadgeTone } from '../components/Badge';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { SkeletonGrid, SkeletonPageHeader } from '../components/Skeleton';
import { useCan } from '../auth/AuthContext';
import { formatEur, formatShortDate } from '../lib/format';
import { useBreakpoint } from '../lib/useBreakpoint';
import { TenantFormDrawer } from './tenants/TenantFormDrawer';
import styles from './TenantsPage.module.css';

const STATUS_TONE: Record<TenantPaymentStatus, BadgeTone> = {
  al_dia: 'success',
  pendiente: 'warning',
  impago: 'danger',
};

/** Estado del CONTRATO, que es lo que dice si el inquilino sigue. */
const LEASE_LABEL: Record<LeaseStatus, string> = {
  borrador: 'Borrador',
  activo: 'Vigente',
  vencido: 'Vencido',
  rescindido: 'Rescindido',
  renovado: 'Renovado',
};

const LEASE_TONE: Record<LeaseStatus, BadgeTone> = {
  borrador: 'warning',
  activo: 'success',
  vencido: 'neutral',
  rescindido: 'danger',
  renovado: 'success',
};

const VISTAS = [
  { key: 'actuales', label: 'Actuales', historic: false },
  { key: 'todos', label: 'Todos', historic: true },
] as const;

type Vista = (typeof VISTAS)[number]['key'];

type Layout = 'rejilla' | 'tabla';

export function TenantsPage() {
  const navigate = useNavigate();
  const canCreate = useCan('tenant:create');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const vista: Vista = searchParams.get('historicos') === '1' ? 'todos' : 'actuales';
  const anchura = useBreakpoint();
  /*
   * En móvil no hay elección: la tabla son seis columnas y no caben. Se fuerzan las fichas y
   * el conmutador se esconde, en vez de dejar un botón que lleva a algo ilegible. El parámetro
   * de la URL se respeta —al girar a horizontal o abrir el enlace en un portátil vuelve la
   * tabla—, simplemente no manda aquí.
   */
  const layoutPedido: Layout = searchParams.get('vista') === 'tabla' ? 'tabla' : 'rejilla';
  const layout: Layout = anchura === 'movil' ? 'rejilla' : layoutPedido;
  const query = searchParams.get('q') ?? '';

  /** Escribe un parámetro en la URL, o lo borra cuando vuelve a su valor por defecto. */
  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    // `replace`: teclear en el buscador no debe llenar el historial letra a letra.
    setSearchParams(next, { replace: true });
  }

  function setVista(key: Vista) {
    setParam('historicos', key === 'todos' ? '1' : null);
  }

  const historic = VISTAS.find((entry) => entry.key === vista)!.historic;
  const params = { limit: 50, ...(historic ? { historic: true } : {}) };

  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.tenants(params),
    queryFn: () => api.tenants(params),
    /**
     * Al cambiar de vista se conservan las fichas anteriores mientras llega la nueva lista.
     * Sin esto la rejilla parpadea a esqueletos por un cambio de filtro, que es un movimiento
     * mucho más brusco de lo que el usuario acaba de pedir.
     */
    placeholderData: (previous) => previous,
  });

  /**
   * La búsqueda filtra SOBRE LA PÁGINA YA CARGADA, no en el servidor.
   *
   * La API de inquilinos no acepta `?q=` —sí la de propiedades— y añadirlo era tocar el
   * backend para algo que con un límite de 50 fichas se resuelve aquí sin latencia ni un
   * `debounce`. Si el listado creciera hasta necesitar paginación de verdad, este es el sitio
   * exacto que habría que cambiar por un parámetro de consulta.
   */
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || !data) return data?.data ?? [];
    return data.data.filter((tenant) =>
      [tenant.fullName, tenant.idDocument, tenant.email, tenant.phone, tenant.property?.name, tenant.placement]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term)),
    );
  }, [data, query]);

  /*
   * POR PISO, NO TODOS A LA VEZ. Con varios pisos, la lista de todos mezclaba a quien vive en
   * Zabalbide con quien vive en JG20, y lo que se busca casi siempre es «¿quién vive en tal
   * piso?». Se elige el piso arriba (`?piso=`) y «Ver todos» los enseña agrupados por piso. Al
   * buscar se busca en todos: quien teclea un nombre no sabe —ni tiene por qué— en qué piso está.
   */
  const rotacion = useQuery({ queryKey: queryKeys.rotation(), queryFn: () => api.rotation() });

  /*
   * Quien terminó su contrato (rescindido, vencido) o está dado de baja sigue existiendo pero no
   * sale en «Actuales». Sin decirlo, parecía que se había borrado solo —y su DNI seguía ocupado
   * al volver a darle de alta—. Se cuenta para avisar con un enlace a «Todos».
   */
  const historicos = useQuery({
    queryKey: queryKeys.tenants({ limit: 100, historic: true }),
    queryFn: () => api.tenants({ limit: 100, historic: true }),
    enabled: !historic,
  });
  const sinVigente = historic
    ? 0
    : (historicos.data?.data ?? []).filter(
        (tenant) =>
          tenant.deletedAt !== null ||
          (tenant.leaseStatus !== null && tenant.leaseStatus !== 'activo' && tenant.leaseStatus !== 'borrador'),
      ).length;
  const avisoHistoricos =
    sinVigente > 0 ? (
      <div className={styles.avisoHistoricos} role="note">
        <Icon name="info" size={15} />
        <span>
          {sinVigente === 1 ? 'Hay 1 inquilino' : `Hay ${sinVigente} inquilinos`} con el contrato terminado o de
          baja que no {sinVigente === 1 ? 'sale' : 'salen'} aquí.
        </span>
        <button type="button" className={styles.avisoHistoricosBoton} onClick={() => setVista('todos')}>
          Ver todos
        </button>
      </div>
    ) : null;
  const grupos = useMemo(() => agruparPorPiso(rows, rotacion.data), [rows, rotacion.data]);
  const pisoPedido = searchParams.get('piso');
  const buscando = query.trim() !== '';
  const pisoActivo: string =
    buscando || pisoPedido === TODOS
      ? TODOS
      : pisoPedido && grupos.some((grupo) => grupo.id === pisoPedido)
        ? pisoPedido
        : (grupos.find((grupo) => grupo.tenants.length > 0)?.id ?? TODOS);
  const visibles = pisoActivo === TODOS ? grupos : grupos.filter((grupo) => grupo.id === pisoActivo);
  const filasVisibles = visibles.flatMap((grupo) => grupo.tenants);

  const selectorPiso =
    grupos.length > 1 ? (
      <PisoPicker
        grupos={grupos}
        activo={pisoActivo}
        total={rows.length}
        buscando={buscando}
        onChange={(id) => setParam('piso', id)}
      />
    ) : null;

  /**
   * El panel viaja CON la cabecera porque su botón vive ahí, y la cabecera se pinta en las
   * cuatro ramas de esta pantalla (cargando, error, vacío y con datos). Declararlo aparte
   * obligaría a repetirlo cuatro veces, y la que se olvidara sería justo la del estado vacío,
   * que es donde más falta hace dar de alta a alguien.
   */
  const header = (
    <>
      <PageHeader
        title="Inquilinos"
        subtitle="Gestiona contratos, documentación y pagos de tus inquilinos."
        actions={
          <div className={styles.headerActions}>
            <div className={styles.search}>
              <span className={styles.searchIcon} aria-hidden="true">
                <Icon name="search" size={16} />
              </span>
              <input
                type="search"
                className={styles.searchInput}
                placeholder="Buscar por nombre, DNI, teléfono…"
                aria-label="Buscar inquilinos"
                value={query}
                onChange={(event) => setParam('q', event.target.value)}
              />
            </div>

            <div className={styles.viewToggle} role="group" aria-label="Qué inquilinos ver">
              {VISTAS.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  className={vista === entry.key ? styles.viewActive : styles.viewButton}
                  onClick={() => setVista(entry.key)}
                  aria-pressed={vista === entry.key}
                >
                  {entry.label}
                </button>
              ))}
            </div>

            {/* Rejilla o tabla. Iconos sin texto: son dos y su forma lo dice todo. */}
            {anchura !== 'movil' && (
            <div className={styles.layoutToggle} role="group" aria-label="Cómo verlos">
              <button
                type="button"
                className={layout === 'rejilla' ? styles.layoutActive : styles.layoutButton}
                onClick={() => setParam('vista', null)}
                aria-pressed={layout === 'rejilla'}
                aria-label="Ver en fichas"
                title="Fichas"
              >
                <Icon name="grid" size={16} />
              </button>
              <button
                type="button"
                className={layout === 'tabla' ? styles.layoutActive : styles.layoutButton}
                onClick={() => setParam('vista', 'tabla')}
                aria-pressed={layout === 'tabla'}
                aria-label="Ver en tabla"
                title="Tabla"
              >
                <Icon name="list" size={16} />
              </button>
            </div>
            )}

            {canCreate && (
              <Button variant="primary" onClick={() => setDrawerOpen(true)} data-tour="alta-inquilino">
                <Icon name="plus" size={17} strokeWidth={2.4} />
                Añadir inquilino
              </Button>
            )}
          </div>
        }
      />
      <TenantFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        /**
         * Se va a su ficha en cuanto existe, y no es un adorno: este listado solo enseña a
         * quien tiene contrato ACTIVO (`historic=false` en la API), así que un inquilino
         * recién creado NO aparecería aquí y el alta parecería no haber hecho nada.
         */
        onCreated={(tenant) => navigate(`/inquilinos/${tenant.id}`)}
      />
    </>
  );

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <SkeletonGrid count={6} height={244} />
      </section>
    );
  }

  if (error) {
    return (
      <section>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  if (rows.length === 0 && query.trim()) {
    return (
      <section>
        {header}
        <EmptyState
          icon="search"
          title="Nadie coincide con esa búsqueda"
          body={`Ningún inquilino de esta lista contiene «${query.trim()}». Prueba con menos letras o cambia a «Todos».`}
          actionLabel="Limpiar la búsqueda"
          onAction={() => setParam('q', null)}
        />
      </section>
    );
  }

  if (data.data.length === 0) {
    return (
      <section>
        {header}
        <EmptyState
          icon="users"
          title={
            historic ? 'Todavía no hay ningún inquilino' : 'Nadie con contrato vigente ahora mismo'
          }
          body={
            historic
              ? 'Da de alta a alguien y después alquílale una habitación desde la ficha del inmueble.'
              : 'Puede que los haya con el contrato terminado: pulsa «Todos» para verlos.'
          }
          {...(canCreate
            ? { actionLabel: 'Añadir inquilino', onAction: () => setDrawerOpen(true) }
            : {})}
        />
        {avisoHistoricos}
      </section>
    );
  }

  if (layout === 'tabla') {
    return (
      <section>
        {header}
        {avisoHistoricos}
        {selectorPiso}
        <TenantsTable rows={filasVisibles} />
      </section>
    );
  }

  return (
    <section>
      {header}
      {avisoHistoricos}
      {selectorPiso}
      {visibles.map((grupo) => (
        <section key={grupo.id} className={styles.grupo} aria-label={grupo.name}>
          <GrupoCabecera grupo={grupo} year={rotacion.data?.year} />
          {grupo.tenants.length === 0 ? (
            <p className={styles.grupoVacio}>Nadie vive aquí ahora mismo.</p>
          ) : (
            <div className={styles.grid}>
              {grupo.tenants.map((tenant) => (
                <TenantGridCard key={tenant.id} tenant={tenant} />
              ))}
            </div>
          )}
        </section>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Por piso
// ---------------------------------------------------------------------------

/** Valor de `?piso=` para ver todos los pisos a la vez. */
const TODOS = 'todos';
/** Id del grupo de los inquilinos sin habitación asignada. */
const SIN_PISO = 'sin-piso';

interface GrupoPiso {
  id: string;
  name: string;
  /** Enlace a la ficha del piso; `null` para «Sin piso». */
  propertyId: string | null;
  tenants: TenantCard[];
  rotation: RotationRow | null;
}

/**
 * Los inquilinos de la lista, por piso. Salen TODOS los pisos (también los que hoy están vacíos,
 * que es justo lo que interesa ver), en orden de nombre, y al final los que no tienen piso.
 */
function agruparPorPiso(rows: TenantCard[], rotacion: RotationResponse | undefined): GrupoPiso[] {
  const grupos = new Map<string, GrupoPiso>();
  for (const fila of rotacion?.data ?? []) {
    grupos.set(fila.propertyId, { id: fila.propertyId, name: fila.name, propertyId: fila.propertyId, tenants: [], rotation: fila });
  }
  for (const tenant of rows) {
    const id = tenant.property?.id ?? SIN_PISO;
    const grupo =
      grupos.get(id) ??
      ({
        id,
        name: tenant.property?.name ?? 'Sin piso asignado',
        propertyId: tenant.property?.id ?? null,
        tenants: [],
        rotation: null,
      } satisfies GrupoPiso);
    grupo.tenants.push(tenant);
    grupos.set(id, grupo);
  }
  return [...grupos.values()].sort((a, b) =>
    a.id === SIN_PISO ? 1 : b.id === SIN_PISO ? -1 : a.name.localeCompare(b.name, 'es', { numeric: true }),
  );
}

function PisoPicker({
  grupos,
  activo,
  total,
  buscando,
  onChange,
}: {
  grupos: GrupoPiso[];
  activo: string;
  total: number;
  buscando: boolean;
  onChange: (id: string) => void;
}) {
  return (
    <div className={styles.pisos} role="group" aria-label="Qué piso ver">
      {/* «Ver todos» va el primero: es la vista general, y los pisos la acotan. */}
      <button
        type="button"
        className={activo === TODOS ? `${styles.piso} ${styles.pisoActivo} ${styles.pisoTodos}` : `${styles.piso} ${styles.pisoTodos}`}
        aria-pressed={activo === TODOS}
        onClick={() => onChange(TODOS)}
      >
        Ver todos
        <span className={styles.pisoCuenta}>{total}</span>
      </button>
      {grupos.map((grupo) => (
        <button
          key={grupo.id}
          type="button"
          className={grupo.id === activo ? `${styles.piso} ${styles.pisoActivo}` : styles.piso}
          aria-pressed={grupo.id === activo}
          onClick={() => onChange(grupo.id)}
          disabled={buscando}
        >
          {grupo.name}
          <span className={styles.pisoCuenta}>{grupo.tenants.length}</span>
        </button>
      ))}
      {buscando && <span className={styles.pisosNota}>Buscando en todos los pisos</span>}
    </div>
  );
}

/** «PU5 · 2 de 3 habitaciones ocupadas · Rotación 2026: 33 %». */
function GrupoCabecera({ grupo, year }: { grupo: GrupoPiso; year: number | undefined }) {
  const ocupadas = grupo.tenants.filter((tenant) => tenant.leaseStatus === 'activo' || tenant.leaseStatus === 'renovado').length;
  const rot = grupo.rotation;
  const meses = rot?.averageStayDays ? Math.max(1, Math.round(rot.averageStayDays / 30.4)) : null;
  return (
    <header className={styles.grupoCabecera}>
      <div className={styles.grupoTitulo}>
        {grupo.propertyId ? (
          <Link to={`/propiedades/${grupo.propertyId}`} className={styles.grupoNombre}>
            {grupo.name}
          </Link>
        ) : (
          <span className={styles.grupoNombre}>{grupo.name}</span>
        )}
        {rot && rot.units > 0 && (
          <span className={styles.grupoOcupacion}>
            {ocupadas} de {rot.units} {rot.units === 1 ? 'habitación ocupada' : 'habitaciones ocupadas'}
          </span>
        )}
      </div>
      {rot && rot.rate !== null && year && (
        <span
          className={styles.rotacion}
          title={`${rot.moveOuts} ${rot.moveOuts === 1 ? 'salida' : 'salidas'} y ${rot.moveIns} ${
            rot.moveIns === 1 ? 'entrada' : 'entradas'
          } en ${year}, para ${rot.units} ${rot.units === 1 ? 'habitación' : 'habitaciones'}${
            meses ? `. Estancia media de los que se fueron: ${meses} ${meses === 1 ? 'mes' : 'meses'}` : ''
          }. Las renovaciones no cuentan.`}
        >
          Rotación {year}
          <strong>{Math.round(rot.rate * 100)} %</strong>
          <span className={styles.rotacionDetalle}>
            · {rot.moveOuts} {rot.moveOuts === 1 ? 'salida' : 'salidas'} · {rot.moveIns} {rot.moveIns === 1 ? 'entrada' : 'entradas'}
          </span>
        </span>
      )}
    </header>
  );
}

/** Un contrato que NO está vigente es lo primero que hay que ver: va en su propia etiqueta. */
const LEASE_WARN = new Set<LeaseStatus>(['borrador', 'vencido', 'rescindido']);

const DOC_LABEL: Record<IdDocumentType, string> = {
  dni: 'DNI',
  nie: 'NIE',
  pasaporte: 'Pasaporte',
};

/**
 * La ficha de cada inquilino en la rejilla.
 *
 * MENOS FILAS, MÁS JERARQUÍA. Antes eran cinco líneas de etiqueta–valor (dos de ellas repetían
 * el inmueble y la habitación que ya dice el subtítulo, y la nacionalidad casi siempre salía
 * vacía) y dos etiquetas de estado apiladas. Ahora:
 *
 *  · Arriba, quién es y dónde vive, con UNA etiqueta: el estado del pago. La del contrato solo
 *    aparece cuando NO está vigente (borrador, vencido, rescindido), que es cuando dice algo.
 *  · En medio, las dos cifras que se buscan: la renta y hasta cuándo dura el contrato.
 *  · Abajo, el documento y el teléfono, y los papeles que tiene subidos.
 */
/** Qué quiere decir cada estado de pago, para leerlo al pasar el cursor. */
const STATUS_HELP: Record<TenantCard['paymentStatus'], string> = {
  al_dia: 'Pagos al día: no debe ninguna renta.',
  pendiente: 'Pago pendiente: tiene una renta emitida que todavía no ha pagado entera.',
  impago: 'Impago: tiene alguna renta vencida sin pagar.',
};

/**
 * Una explicación que sale AL MOMENTO al pasar el cursor (o al enfocar con el teclado). El
 * `title` del navegador tarda un segundo en salir y casi nadie llega a verlo.
 */
function ConAyuda({ texto, children, enfocable = true }: { texto: string; children: ReactNode; enfocable?: boolean }) {
  return (
    <span className={styles.ayuda} tabIndex={enfocable ? 0 : undefined} aria-label={texto}>
      {children}
      <span className={styles.ayudaTexto} role="tooltip">
        {texto}
      </span>
    </span>
  );
}

function TenantGridCard({ tenant }: { tenant: TenantCard }) {
  const leaseWarn = tenant.leaseStatus ? LEASE_WARN.has(tenant.leaseStatus) : true;
  const docsOk = tenant.documentBadges.filter((badge) => badge.present).length;
  const expiry = tenant.leaseExpiry;
  const expiryLabel = !expiry
    ? null
    : expiry.daysToEnd < 0
      ? `Contrato vencido el ${formatShortDate(expiry.endDate)}, sin renovación ni rescisión`
      : expiry.daysToEnd === 0
        ? 'El contrato termina hoy, sin renovación ni rescisión'
        : `Al contrato le ${expiry.daysToEnd === 1 ? 'queda 1 día' : `quedan ${expiry.daysToEnd} días`}, sin renovación ni rescisión`;

  return (
    <article className={styles.card}>
      <div className={styles.cardHead}>
        <Avatar initials={tenant.initials} size={44} fontSize={15} />
        <div className={styles.name}>
          {/*
            UN SOLO ENLACE, el del nombre, con su área de pulsación estirada sobre toda la ficha
            (`::after`): se pulsa en cualquier sitio sin anidar elementos interactivos. El enlace
            al inmueble y el teléfono quedan por encima y se pulsan por su cuenta.
          */}
          <Link to={`/inquilinos/${tenant.id}`} className={styles.nameMain}>
            {tenant.fullName}
          </Link>
          <div className={styles.nameSub}>
            {tenant.property ? (
              <Link to={`/propiedades/${tenant.property.id}`} className={styles.propertyLink}>
                {tenant.property.name}
              </Link>
            ) : (
              'Sin habitación asignada'
            )}
            {tenant.property && tenant.room ? <span> · Hab. {tenant.room}</span> : null}
          </div>
        </div>
        <div className={styles.status}>
          {/* Dado de baja dice más que el estado de su último contrato: va en su lugar. */}
          {tenant.deletedAt ? (
            <Badge tone="neutral" size="sm">
              Dado de baja
            </Badge>
          ) : (
            leaseWarn && (
              <Badge tone={tenant.leaseStatus ? LEASE_TONE[tenant.leaseStatus] : 'neutral'} size="sm">
                {tenant.leaseStatus ? LEASE_LABEL[tenant.leaseStatus] : 'Sin contrato'}
              </Badge>
            )
          )}
          {/* Contrato a menos de tres meses de acabar: un punto, y la frase al pasar por encima. */}
          {expiry && !tenant.deletedAt && (
            <ConAyuda texto={expiryLabel!}>
              <span
                className={expiry.daysToEnd < 0 ? `${styles.expiryDot} ${styles.expiryDotPast}` : styles.expiryDot}
                aria-hidden="true"
              />
            </ConAyuda>
          )}
          <ConAyuda texto={STATUS_HELP[tenant.paymentStatus]}>
            <Badge tone={STATUS_TONE[tenant.paymentStatus]} size="sm">
              {tenant.paymentStatusLabel}
            </Badge>
          </ConAyuda>
        </div>
      </div>

      <dl className={styles.figures}>
        <div className={styles.figure}>
          <dt>Renta</dt>
          <dd>
            {tenant.monthlyRent ? (
              <>
                {formatEur(tenant.monthlyRent)}
                <span className={styles.figureUnit}>/mes</span>
              </>
            ) : (
              <span className={styles.figureNil}>—</span>
            )}
          </dd>
        </div>
        <div className={styles.figure}>
          <dt>Contrato hasta</dt>
          <dd>
            {tenant.leaseEndDate ? (
              formatShortDate(tenant.leaseEndDate)
            ) : (
              <span className={styles.figureNil}>—</span>
            )}
          </dd>
        </div>
      </dl>

      <div className={styles.contact}>
        <span className={styles.contactItem}>
          <span className={styles.contactLabel}>{DOC_LABEL[tenant.idDocumentType] ?? 'DNI'}</span>
          {tenant.idDocument ?? <span className={styles.figureNil}>sin número</span>}
        </span>
        {tenant.phone && (
          <a href={`tel:${tenant.phone.replace(/\s+/g, '')}`} className={styles.phone}>
            {tenant.phone}
          </a>
        )}
      </div>

      <div className={styles.docs}>
        <span className={styles.docsCount}>
          Papeles {docsOk}/{tenant.documentBadges.length}
        </span>
        <span className={styles.docsList}>
          {tenant.documentBadges.map((badge) => (
            <span
              key={badge.label}
              className={badge.present ? `${styles.doc} ${styles.docOk}` : styles.doc}
              title={badge.present ? `${badge.label}: subido` : `${badge.label}: falta`}
            >
              {badge.present ? (
                <Icon name="check" size={12} strokeWidth={2.6} />
              ) : (
                <span className={styles.docDot} aria-hidden="true" />
              )}
              {badge.label}
              <span className="naar-sr-only">{badge.present ? ' subido' : ' falta'}</span>
            </span>
          ))}
        </span>
      </div>
    </article>
  );
}

/**
 * Vista de tabla.
 *
 * Las mismas fichas, en filas. Se escribe con CSS Grid y roles ARIA en vez de con `<table>`
 * por la misma razón que el resto de tablas de la aplicación: la plantilla de columnas del
 * maquetado no cabe en una tabla nativa sin desalinearse, y los roles le devuelven al lector
 * de pantalla la semántica que el marcado pierde.
 *
 * El contenedor tiene su propio desplazamiento horizontal: en un portátil estrecho se desliza
 * la tabla, NUNCA la página entera.
 */
function TenantsTable({ rows }: { rows: TenantCard[] }) {
  return (
    <Card variant="table">
      <div className={styles.tableScroll}>
        <div className={styles.table} role="table" aria-label="Inquilinos">
          <div className={styles.tableHead} role="row">
            <span role="columnheader">Inquilino</span>
            <span role="columnheader">Documento</span>
            <span role="columnheader">Inmueble</span>
            <span role="columnheader">Renta</span>
            <span role="columnheader">Contrato</span>
            <span role="columnheader">Pago</span>
          </div>

          {rows.map((tenant) => (
            <Link
              key={tenant.id}
              to={`/inquilinos/${tenant.id}`}
              className={styles.tableRow}
              role="row"
            >
              <span className={styles.cellIdentity} role="cell">
                <Avatar initials={tenant.initials} size={34} fontSize={13} />
                <span className={styles.cellIdentityText}>
                  <strong>{tenant.fullName}</strong>
                  <span>{tenant.placement ?? 'Sin habitación asignada'}</span>
                </span>
              </span>
              <span className={styles.cellMuted} role="cell">
                {tenant.idDocument ?? '—'}
              </span>
              <span className={styles.cellMuted} role="cell">
                {tenant.property?.name ?? '—'}
              </span>
              <span className={styles.cellNumber} role="cell">
                {tenant.monthlyRent ? formatEur(tenant.monthlyRent) : '—'}
              </span>
              <span role="cell">
                {tenant.deletedAt ? (
                  <Badge tone="neutral" size="sm">
                    Dado de baja
                  </Badge>
                ) : (
                  <Badge
                    tone={tenant.leaseStatus ? LEASE_TONE[tenant.leaseStatus] : 'neutral'}
                    size="sm"
                  >
                    {tenant.leaseStatus ? LEASE_LABEL[tenant.leaseStatus] : 'Sin contrato'}
                  </Badge>
                )}
              </span>
              <span role="cell">
                {/* Dentro del enlace de la fila: se lee al pasar el cursor, sin otro foco. */}
                <ConAyuda texto={STATUS_HELP[tenant.paymentStatus]} enfocable={false}>
                  <Badge tone={STATUS_TONE[tenant.paymentStatus]} size="sm">
                    {tenant.paymentStatusLabel}
                  </Badge>
                </ConAyuda>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </Card>
  );
}
